import {
  Box,
  Button,
  Card,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Pagination } from "@components/ui-kit/table/pagination";
import { Trans } from "@lingui/macro";
import { usePushNotifications } from "@src/components/usePushNotifications";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { Fragment, useEffect, useState } from "react";
import { HeaderContainer, NotificationList, TaskList } from "./components";
import { useFetchNotifications } from "./hooks";
import { NotificationSettings } from "./NotificationSettings";
import { TaskNotificationsSettings } from "./TaskNotificationsSettings";

const Table = () => {
  const { notificationsStore: store } = useStore();
  const [permissionsDenied, setPermissionsDenied] = useState(false);

  useFetchNotifications(store);

  useEffect(() => {
    store.refetchTaskNotifications(true);

    const interval = setInterval(() => {
      // refetch every 30 seconds
      store.refetchTaskNotifications(true);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const { isSubscribed, requestPermission, subscriptionPending } =
    usePushNotifications({
      onSub: () => {
        setPermissionsDenied(false);
      },
      onSubFail: () => {},
      onDenied: () => {
        setPermissionsDenied(true);
      },
    });

  return (
    <Fragment>
      <Stack
        direction={{
          base: "column",
          md: "row",
        }}
        h="full"
        spacing="5"
      >
        <Box flex="1">
          <HeaderContainer forModule="table">
            <h1 className="font-bold text-4xl">
              <Trans>Tasks & communications</Trans>
            </h1>
            <TaskNotificationsSettings
              notificationCount={store.taskNotifications.length}
              showRead={store.showReadTaskNotifications}
              onMark={store.refetchTaskNotifications}
              isLoading={store.taskNotificationsLoading}
              onShowReadChanged={() => {
                store.setShowReadTaskNotifications(
                  !store.showReadTaskNotifications,
                );
                store.refetchTaskNotifications(true);
              }}
            />
          </HeaderContainer>
          <TaskList
            taskNotifications={store.taskNotifications}
            isLoading={store.taskNotificationsLoading}
          />
          <Card>
            <Pagination
              perPageVisible={false}
              store={store.taskNotificationPagination}
            />
          </Card>
        </Box>
        <Box flex="1">
          <HeaderContainer forModule="table">
            <HStack spacing="2">
              <h1 className="font-bold text-4xl">
                <Trans>Notifications</Trans>
              </h1>
            </HStack>
            <NotificationSettings store={store} />
          </HeaderContainer>
          <Card>
            <NotificationList
              items={store.visibleNotifications}
              loading={store.isLoading}
            />
            <Pagination perPageVisible={false} store={store.pagination} />
          </Card>
        </Box>
      </Stack>
      <VStack align="flex-end" mt="4">
        {!isSubscribed && (
          <Fragment>
            <Button
              colorScheme="grey"
              isLoading={subscriptionPending}
              onClick={requestPermission}
              variant="outline"
            >
              <Trans>Enable push notifications</Trans>
            </Button>
            {permissionsDenied && (
              <Text maxW={{ base: "full", md: "50%" }} color="red.500">
                <Trans>
                  Notifications are blocked. Please allow them manually by
                  clicking on the lock button near URL. After allowing them,
                  please try again.
                </Trans>
              </Text>
            )}
          </Fragment>
        )}
      </VStack>
    </Fragment>
  );
};

export default observer(Table);
